/** Named icon catalogue. Add new icons here as the app grows. */
type IconName =
  | "chevron-left"
  | "chevron-right"
  | "chevron-down"
  | "arrow-left"
  | "search"
  | "close"
  | "directions"
  | "send";

interface IconDef {
  viewBox: string;
  width: number;
  height: number;
  /** Single path. For multi-shape icons, use `paths` instead. */
  d?: string;
  /** Multiple stroke paths (used by icons like search where one path
      isn't enough). */
  paths?: readonly string[];
}

const ICONS: Record<IconName, IconDef> = {
  "chevron-left": {
    viewBox: "0 0 16 16",
    width: 16,
    height: 16,
    d: "M10 12L6 8L10 4",
  },
  "chevron-right": {
    viewBox: "0 0 16 16",
    width: 16,
    height: 16,
    d: "M6 4L10 8L6 12",
  },
  "chevron-down": {
    viewBox: "0 0 16 16",
    width: 16,
    height: 16,
    d: "M4 6L8 10L12 6",
  },
  "arrow-left": {
    viewBox: "0 0 20 14",
    width: 20,
    height: 14,
    d: "M18 7H2M2 7L8 1M2 7L8 13",
  },
  search: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    paths: [
      "M10.5 17C14.0899 17 17 14.0899 17 10.5C17 6.91015 14.0899 4 10.5 4C6.91015 4 4 6.91015 4 10.5C4 14.0899 6.91015 17 10.5 17Z",
      "M20 20L15.1 15.1",
    ],
  },
  close: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    paths: ["M6 6L18 18", "M6 18L18 6"],
  },
  // Figma node 228:18187 — the "turn-by-turn" arrow used as the prefix
  // glyph on the "Directions to spot" button. Drawn in an 18-unit viewBox
  // (Figma's raw export size) so the stroke lands on whole pixels when
  // the icon renders at its intrinsic 18 px.
  directions: {
    viewBox: "0 0 18 18",
    width: 18,
    height: 18,
    d: "M12 1L17 6L12 11M17 6H5C3.93913 6 2.92172 6.42143 2.17157 7.17157C1.42143 7.92172 1 8.93913 1 10V17",
  },
  // Paper-plane glyph used as the prefix icon on the modal "Send" button.
  // Outline-only so it inherits the icon-width stroke convention.
  send: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    paths: ["M22 2L11 13", "M22 2L15 22L11 13L2 9L22 2Z"],
  },
};

interface IconProps {
  name: IconName;
  className?: string | undefined;
}

export function Icon({ name, className }: IconProps) {
  const icon = ICONS[name];
  const paths = icon.paths ?? (icon.d ? [icon.d] : []);
  return (
    <svg
      width={icon.width}
      height={icon.height}
      viewBox={icon.viewBox}
      fill="none"
      aria-hidden
      className={className}
      style={{ strokeWidth: "var(--icon-width)" }}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
